import { MediaFolderTree } from './MediaFolderTree';
import { requiredTextWithinLimit, textWithinLimit } from '@/shared/lib/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { MediaAssetResponse, MediaFolderResponse } from '@/core/generated/api';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import {
    formatMediaDimensions,
    formatMediaFileCount,
    formatMediaSize,
    formatMediaType,
    getMediaFolderPath,
} from '@/entities/MediaLibrary/model/format';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import { useImageDimensions } from '@/shared/hooks/useImageDimensions';
import classes from './MediaLibrary.module.scss';

export type MediaEditMode = 'folder' | 'rename-folder' | 'rename' | 'move' | 'tags';

interface MediaEditDialogProps {
    mode: MediaEditMode;
    folders: MediaFolderResponse[];
    folder?: MediaFolderResponse;
    parentId?: string;
    assets: MediaAssetResponse[];
    onClose: () => void;
    onSaved: () => void;
}

const schema = z.object({
    name: requiredTextWithinLimit('Название', 200).refine(value => !/[\\/]/.test(value) && value !== '.' && value !== '..', 'Недопустимое название'),
    folderId: z.string(),
    tags: textWithinLimit(1100, 'Не более 20 тегов по 50 символов'),
});

type MediaEditValues = z.infer<typeof schema>;
const { TextField, SelectField } = createRHFFields<MediaEditValues>('media-edit');

const TITLES: Record<MediaEditMode, string> = {
    folder: 'Новая папка',
    'rename-folder': 'Изменить папку',
    rename: 'Переименовать',
    move: 'Переместить файлы',
    tags: 'Добавить теги'
};

/** Редактирует папку, название файла или групповое действие */
export const MediaEditDialog = (
    { mode, folders, folder, parentId, assets, onClose, onSaved }: MediaEditDialogProps
) => {
    const asset = assets[0];
    const { recordDimensions, getDimensions } = useImageDimensions();
    const isFolder = mode === 'folder' || mode === 'rename-folder';

    const form = useForm<MediaEditValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: folder?.name ?? asset?.name ?? (isFolder
                ? ''
                : 'Файлы'),
            folderId: (isFolder && folder
                ? folder.parentId
                : parentId) ?? '',
            tags: '',
        },
    });

    const busy = form.formState.isSubmitting;
    const requiresChanges = mode === 'rename' || mode === 'rename-folder';
    const folderId = form.watch('folderId');
    const folderName = form.watch('name').trim();
    const folderPath = [getMediaFolderPath(folders, folderId), folderName].filter(Boolean).join(' / ');

    const options = [{
        name: 'Все файлы',
        id: ''
    }, ...folders.filter(item => item.id !== folder?.id)
        .map(item => ({
            name: getMediaFolderPath(folders, item.id),
            id: item.id ?? ''
        }))];

    /** Сохраняет действие с проверкой серверной версии */
    const submit = async (values: MediaEditValues): Promise<void> => {
        if (requiresChanges && !form.formState.isDirty) return;

        if (isFolder && values.name.length > 100) {
            form.setError('name', { message: 'Не более 100 символов' });

            return;
        }

        if (mode === 'tags' && values.tags.split(',').every(tag => !tag.trim())) {
            form.setError('tags', { message: 'Укажите хотя бы один тег' });

            return;
        }

        try {
            if (isFolder) await mediaLibraryService.saveFolder({
                name: values.name,
                parentId: values.folderId || null,
                version: folder?.version
            }, folder?.id);
            else if (mode === 'rename' && asset?.id) await mediaLibraryService.update(asset.id, {
                name: values.name,
                folderId: asset.folderId,
                tags: asset.tags ?? [],
                version: asset.version
            });
            else await mediaLibraryService.bulk({
                items: assets.map(item => ({
                    id: item.id,
                    version: item.version
                })),
                move: mode === 'move',
                folderId: values.folderId || null,
                addTags: mode === 'tags'
                    ? values.tags.split(',').map(tag => tag.trim()).filter(Boolean)
                    : null
            });

            onSaved();
        } catch (error: unknown) {
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось сохранить изменения') });
        }
    };

    return <Modal
        appearance="surface"
        visible
        title={mode === 'move'
            ? `Переместить ${formatMediaFileCount(assets.length)}`
            : TITLES[mode]
        }
        onHide={onClose}
        closable={!busy}
        size="md"
        className={mode === 'rename'
            ? classes.renameDialog
            : undefined
        }
    >
        <FormProvider {...form}>
            <form
                className={classes.dialogForm}
                onSubmit={form.handleSubmit(submit)}
                noValidate
            >
                {mode !== 'move' && mode !== 'rename' &&
                    <div className={classes.dialogSymbol}>
                        <i
                            aria-hidden="true"
                            className={isFolder
                                ? 'pi pi-folder'
                                : 'pi pi-file-edit'
                            }
                        />
                    </div>
                }
                {isFolder &&
                    <TextField
                        name="name"
                        label="Название папки"
                        maxLength={100}
                        isRequired
                        disabled={busy}
                    />
                }
                {mode === 'rename' &&
                    <div className={classes.renamePreview}>
                        <img
                            src={asset?.url ?? ''}
                            alt={asset?.name ?? 'Изображение'}
                        />
                        <div className={classes.renameFields}>
                            <TextField
                                name="name"
                                label="Название"
                                maxLength={200}
                                disabled={busy}
                            />
                            <p className={classes.note}>Название в медиатеке. Ссылки и размещение в услугах сохранятся</p>
                            <p className={classes.originalName}>Исходный файл: {asset?.originalName || 'Имя неизвестно'}
                            </p>
                        </div>
                    </div>
                }
                {isFolder &&
                    <>
                        <SelectField
                            name="folderId"
                            label={isFolder
                                ? 'Родительская папка'
                                : 'Папка назначения'
                            }
                            options={options}
                            disabled={busy}
                        />
                        <div
                            className={classes.folderPath}
                            aria-live="polite"
                        >
                            <span>Путь к папке</span>
                            <p>
                                <i
                                    className="pi pi-folder"
                                    aria-hidden="true"
                                />
                                {folderPath}
                            </p>
                        </div>
                    </>
                }
                {mode === 'move' &&
                    <>
                        <ul
                            className={classes.selectionPreview}
                            aria-label="Выбранные изображения"
                        >
                            {assets.map(item => {
                                const dimensions = getDimensions(item.url, item.width, item.height);

                                return <li
                                    key={item.id}
                                    className={classes.selectionItem}
                                >
                                    <img
                                        src={item.url ?? ''}
                                        alt={item.name ?? 'Изображение'}
                                        onLoad={event => recordDimensions(item.url, event.currentTarget)}
                                    />
                                    <div>
                                        <strong title={item.name ?? ''}>
                                            {item.name}
                                        </strong>
                                        <small>
                                            {formatMediaType(item.contentType)} · {formatMediaSize(item.size)}
                                        </small>
                                        <small>
                                            {formatMediaDimensions(dimensions?.width, dimensions?.height)}
                                        </small>
                                    </div>
                                </li>;
                            })}
                        </ul>
                        <h3 className={classes.moveLabel}>Выберите папку назначения</h3>
                        <div className={classes.moveTree}>
                            <MediaFolderTree
                                folders={folders}
                                selected={folderId || undefined}
                                disabled={busy}
                                onSelect={id => form.setValue('folderId', id ?? '', { shouldDirty: true })}
                            />
                        </div>
                        <div className={classes.moveSummary}>
                            <p>Папка назначения: <strong>
                                {getMediaFolderPath(folders, folderId)}
                            </strong>
                            </p>
                            <p>Ссылки на изображения сохранятся</p>
                        </div>
                    </>
                }
                {mode === 'tags' &&
                    <TextField
                        name="tags"
                        label="Теги через запятую"
                        placeholder="микс, услуги, питомцы"
                        disabled={busy}
                    />
                }
                {mode === 'tags' &&
                    <p className={classes.note}>Постоянные ссылки и ключи S3 сохранятся. Выбрано файлов: {assets.length}.</p>
                }
                {form.formState.errors.root?.server?.message &&
                    <RequestError message={form.formState.errors.root.server.message} />
                }
                <div className={classes.dialogFooter}>
                    <ActionButton
                        type="button"
                        color="secondary"
                        outlined
                        onClick={onClose}
                        disabled={busy}
                    >
                        Отмена
                    </ActionButton>
                    <ActionButton
                        type="submit"
                        loading={busy}
                        disabled={requiresChanges && !form.formState.isDirty}
                    >
                        {isFolder && !folder
                            ? 'Создать папку'
                            : mode === 'move'
                                ? 'Переместить'
                                : 'Сохранить'}
                    </ActionButton>
                </div>
            </form>
        </FormProvider>
    </Modal>;
};
